/* eslint-disable unicorn/filename-case */
import { type useForm } from '@tanstack/react-form';
import { RefreshCcw01, Stars01 } from '@untitled-ui/icons-react';
import { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { type ZodType, z } from 'zod';

import { Button } from '../../../components/buttons/button';
import { GoogleIcon } from '../../../components/icons/google-icon';
import { TextField } from '../../../components/inputs/text-field';
import { Loading } from '../../../components/loading';
import { type GeneratePrimaryKeywordSocketRes } from '../../../entity/api/routes/article';
import { trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import { generateKeyword } from '../../../sdk/article';
import { tNamespace } from '../../../services/translation';
import { Feature, isFeatureAvailable } from '../../../shared/feature';
import { parseFirstZodError } from '../../../shared/validation';
import {
  type KeywordSuggestion,
  KeywordSuggestionsDropdown,
} from './-keyword-suggestions-dropdown';

const t = tNamespace('app.blogs.-section-modal-generation');

enum KeywordGenerationStatus {
  NotStarted = 'not-started',
  Generating = 'generating',
  Generated = 'generated',
  Error = 'error',
}

const keywordLabel: Record<KeywordGenerationStatus, string> = {
  [KeywordGenerationStatus.NotStarted]: t('generateKeyword'),
  [KeywordGenerationStatus.Generating]: t('generating'),
  [KeywordGenerationStatus.Generated]: t('generated'),
  [KeywordGenerationStatus.Error]: t('error'),
};

const icon = {
  [KeywordGenerationStatus.NotStarted]: <Stars01 width={16} height={16} />,
  [KeywordGenerationStatus.Generating]: <Loading size="xs" />,
  [KeywordGenerationStatus.Generated]: <RefreshCcw01 width={16} height={16} />,
  [KeywordGenerationStatus.Error]: <RefreshCcw01 width={16} height={16} />,
};

type RewriteKeywordFormProps = {
  form: ReturnType<
    typeof useForm<{
      country: string;
      language: string;
      chosenTitle: string;
      providedContentType: 'url' | 'text';
      url: string;
      tiptapJsonContent: Record<string, any> | null;
      primaryKeyword: string;
      primaryPrompt: string;
      mode: 'light' | 'major';
      contentLength: 'auto' | 'same' | 'custom';
      contentLengthValue: number;
      extraInformation: string;
    }>
  >;
  brandId: string;
};

export function RewriteKeywordForm(props: RewriteKeywordFormProps) {
  const { form, brandId } = props;
  const keywordInputRef = useRef<HTMLDivElement>(null);
  const [showKeywordSuggestions, setShowKeywordSuggestions] = useState(false);
  const [keywordSuggestions, setKeywordSuggestions] = useState<KeywordSuggestion[]>([]);
  const [keywordGenerationStatus, setKeywordGenerationStatus] = useState<KeywordGenerationStatus>(
    KeywordGenerationStatus.NotStarted
  );
  const [keywordGenerationError, setKeywordGenerationError] = useState<string | null>(null);

  function generatePrimaryKeyword() {
    if (keywordGenerationStatus === KeywordGenerationStatus.Generating) {
      return;
    }
    setShowKeywordSuggestions(false);
    setKeywordGenerationStatus(KeywordGenerationStatus.Generating);
    setKeywordGenerationError(null);

    let generationPromise: Promise<GeneratePrimaryKeywordSocketRes> | null = null;
    try {
      generationPromise = generateKeyword({
        brandId: brandId,
        topic: form.state.values.chosenTitle,
        previousKeywords: form.state.values.primaryKeyword,
        language: form.state.values.language,
        country: form.state.values.country,
      });
    } catch (err) {
      captureException(
        err,
        {
          file: 'app/blogs/-rewrite-keyword-form.tsx',
          method: 'generatePrimaryKeyword',
          infoMessage: 'unable to create generation promise',
        },
        trackError
      );
      setKeywordGenerationStatus(KeywordGenerationStatus.Error);
      setKeywordGenerationError('UNABLE_TO_GENERATE_KEYWORD');
    }

    // Handle the promise resolution/rejection when the socket event arrives
    generationPromise
      ?.then(function (response) {
        setKeywordGenerationStatus(KeywordGenerationStatus.Generated);
        const { result } = response;
        if (result != null && 'primaryKeywordChoices' in result) {
          const { primaryKeywordChoices } = result;
          const sortedPrimaryKeywordChoices = primaryKeywordChoices.sort(function (a, b) {
            return (b.volume ?? 0) - (a.volume ?? 0);
          });
          if (sortedPrimaryKeywordChoices.length > 0) {
            setKeywordSuggestions(sortedPrimaryKeywordChoices);
            setShowKeywordSuggestions(true);
          }
        }

        if (result != null && 'generatedPrimaryKeyword' in result) {
          form.setFieldValue('primaryKeyword', result.generatedPrimaryKeyword);
          void form.validateField('primaryKeyword', 'change');
        }
      })
      .catch(function (err) {
        setKeywordGenerationStatus(KeywordGenerationStatus.Error);
        setKeywordGenerationError('UNABLE_TO_GENERATE_KEYWORD');
        captureException(
          err,
          {
            file: 'app/blogs/-rewrite-keyword-form.tsx',
            method: 'generatePrimaryKeyword',
            infoMessage: 'unable to generate primary keyword',
          },
          trackError
        );
      });
  }

  function ButtonKeywordGeneration(props: {
    generationType: KeywordGenerationStatus;
    isDisabled: boolean;
    className?: string;
    generationAction: () => void;
  }) {
    const className = twMerge(
      props.isDisabled
        ? 'w-full whitespace-nowrap text-branding-gray-200 cursor-not-allowed hover:bg-transparent'
        : 'w-full whitespace-nowrap hover:cursor-pointer',
      props.className
    );
    const label = keywordLabel[props.generationType];

    if (props.generationType === KeywordGenerationStatus.Generating) {
      return (
        <div className={'mr-2 flex items-center gap-2 text-branding-accent-700'}>
          {icon[props.generationType]}
          <p className="font-body-2-sb text-branding-accent-700">{label}</p>
        </div>
      );
    }
    return (
      <Button
        size="sm"
        variant={
          props.generationType === KeywordGenerationStatus.Generated ||
          !isFeatureAvailable(Feature.GEO_INFORMATION)
            ? 'ghost-accent'
            : 'gradient-premium'
        }
        className={className}
        leftIcon={icon[props.generationType]}
        onClick={function () {
          if (props.isDisabled) {
            return;
          }
          props.generationAction();
        }}
      >
        {label}
      </Button>
    );
  }

  return (
    <div className="mb-6 flex flex-col gap-4">
      <div className="relative" ref={keywordInputRef}>
        <form.Field
          name="primaryKeyword"
          validators={{
            onChange: function (data) {
              return parseFirstZodError(
                z.string().min(1, 'KEYWORD_REQUIRED') as ZodType<string>,
                data.value
              );
            },
          }}
          // eslint-disable-next-line react/no-children-prop
          children={function (field) {
            return (
              <form.Subscribe
                // eslint-disable-next-line react/no-children-prop
                children={function () {
                  return (
                    <TextField
                      name={field.name}
                      sizeField="md"
                      label={
                        <div className="flex items-center gap-1">
                          {t('seoKeyword')}
                          <GoogleIcon useOriginalColors={true} size={12} />
                        </div>
                      }
                      placeholder={t('keywordRankOnGoogle')}
                      value={field.state.value}
                      onChange={function (e) {
                        field.handleChange(e.target.value);
                        // Close suggestions when user types manually
                        if (showKeywordSuggestions) {
                          setShowKeywordSuggestions(false);
                        }
                      }}
                      overrideErrorIcon={true}
                      containerClassName="pr-1"
                      error={
                        field.state.meta?.errors.length > 0
                          ? field.state.meta?.errors
                          : keywordGenerationError
                      }
                      autoComplete="off"
                      disabled={keywordGenerationStatus === KeywordGenerationStatus.Generating}
                      iconRight={
                        <ButtonKeywordGeneration
                          generationType={keywordGenerationStatus}
                          generationAction={generatePrimaryKeyword}
                          isDisabled={false}
                        />
                      }
                    />
                  );
                }}
              />
            );
          }}
        />
        <KeywordSuggestionsDropdown
          isOpen={showKeywordSuggestions}
          suggestions={keywordSuggestions}
          triggerRef={keywordInputRef}
          onSelect={function (suggestion: KeywordSuggestion) {
            form.setFieldValue('primaryKeyword', suggestion.primaryKeyword);
            void form.validateField('primaryKeyword', 'change');
            setShowKeywordSuggestions(false);
            setKeywordSuggestions([]);
          }}
          onClose={function () {
            setKeywordSuggestions([]);
            setShowKeywordSuggestions(false);
          }}
        />
      </div>
    </div>
  );
}
